/**
 * HTTP CLIENT STANDARD
 * ====================
 * This service uses apiClient from api.service.ts as the standardized HTTP client.
 *
 * WHY apiClient:
 * - Automatic CSRF token injection via request interceptors
 * - Automatic retry on 403 CSRF errors
 * - Centralized error handling and logging
 * - Type-safe responses
 * - Session cookie management (withCredentials: true)
 *
 * DO NOT use:
 * - Raw axios (missing interceptors)
 * - Native fetch (manual CSRF, no retry logic)
 * - Custom HTTP implementations
 *
 * See: /HTTP_CLIENT_STANDARDS.md
 *
 * MIGRATED: 2025-10-09 - Converted from fetch to apiClient
 * REASON: Fix 403 Forbidden errors due to inconsistent CSRF handling
 */

import type {
  Customer,
  CreateCustomerDto,
  UpdateCustomerDto,
  CustomerListParams,
  CustomerListResponse,
} from '../types/customer';
import type {
  ServiceHistoryEntry,
  ServiceHistoryCreateDto,
  ServiceHistoryUpdateDto,
} from '../types/serviceHistory.types';
import { apiClient } from './api.service';
import { logEnhancedError } from '../utils/errorDebugger';
import { logger } from '../utils/logger';
import { mapSortFieldToBackend } from './fieldMappings';

class CustomerService {
  private readonly basePath = '/api/v1/customers';

  /**
   * Transform API response to frontend Customer type
   *
   * NOTE: The response interceptor already converts snake_case to camelCase,
   * so we just need to ensure the data matches our Customer interface.
   */
  private transformCustomer(apiCustomer: any): Customer {
    return {
      id: apiCustomer.id,
      name: apiCustomer.name,
      email: apiCustomer.email,
      phone: apiCustomer.phone,
      address: apiCustomer.address,
      notes: apiCustomer.notes,
      truckCount: apiCustomer.truckCount || 0,
      trucksDueWithin2Weeks: apiCustomer.trucksDueWithin2Weeks || 0,
      trucksDueWithin2To6Weeks: apiCustomer.trucksDueWithin26Weeks || 0,
      trucksDueWithin6To12Weeks: apiCustomer.trucksDueWithin612Weeks || 0,
      createdAt: apiCustomer.createdAt,
      updatedAt: apiCustomer.updatedAt,
    };
  }

  /**
   * Fetch paginated list of customers
   */
  async getCustomers(params?: CustomerListParams): Promise<CustomerListResponse> {
    const startTime = Date.now();

    try {
      // Filter out undefined/null values to avoid sending invalid params
      const cleanParams = Object.entries({
        page: params?.page,
        per_page: params?.limit,
        search: params?.search?.trim(),
        sort_by: params?.sortBy ? mapSortFieldToBackend(params.sortBy) : undefined,
        sort_order: params?.sortOrder,
      }).reduce((acc, [key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          acc[key] = value;
        }
        return acc;
      }, {} as Record<string, any>);

      logger.debug('Fetching customers list', {
        component: 'CustomerService',
        action: 'getCustomers',
        params: cleanParams,
      });

      const response = await apiClient.get<any>(this.basePath, {
        params: cleanParams,
      });

      const duration = Date.now() - startTime;

      logger.debug('Customers fetched successfully', {
        component: 'CustomerService',
        action: 'getCustomers',
        duration: `${duration}ms`,
        count: response.data.items?.length || 0,
        total: response.data.total,
      });

      // Transform API response to match frontend types
      // NOTE: The interceptor has already converted snake_case to camelCase
      return {
        customers: response.data.items.map(this.transformCustomer),
        total: response.data.total,
        page: response.data.page,
        limit: response.data.perPage,
        totalPages: response.data.totalPages,
      };
    } catch (error) {
      const duration = Date.now() - startTime;

      // Log enhanced error with full context
      const enhancedError = logEnhancedError(error as any, {
        component: 'CustomerService',
        action: 'getCustomers',
        duration,
      });

      // Log additional context about what we were trying to do
      logger.error('Failed to fetch customers', {
        component: 'CustomerService',
        action: 'getCustomers',
        params,
        duration: `${duration}ms`,
        errorType: enhancedError.constructor.name,
        status: enhancedError.context?.status,
        url: enhancedError.context?.url,
      });

      throw enhancedError;
    }
  }

  /**
   * Fetch single customer by ID
   */
  async getCustomer(id: string, includeVehicles: boolean = true): Promise<Customer> {
    try {
      logger.debug('Fetching customer', {
        component: 'CustomerService',
        action: 'getCustomer',
        customerId: id,
        includeVehicles,
      });

      const response = await apiClient.get<any>(`${this.basePath}/${id}`, {
        params: { include_vehicles: includeVehicles },
      });

      logger.debug('Customer fetched successfully', {
        component: 'CustomerService',
        action: 'getCustomer',
        customerId: id,
        vehicleCount: response.data.vehicles?.length || 0,
      });

      // Transformation handled automatically by apiClient interceptors
      return response.data;
    } catch (error) {
      const enhancedError = logEnhancedError(error as any, {
        component: 'CustomerService',
        action: 'getCustomer',
      });

      logger.error('Failed to fetch customer', {
        component: 'CustomerService',
        action: 'getCustomer',
        customerId: id,
        status: enhancedError.context?.status,
      });

      throw enhancedError;
    }
  }

  /**
   * Create new customer
   */
  async createCustomer(data: CreateCustomerDto): Promise<Customer> {
    const response = await apiClient.post<Customer>(this.basePath, data);
    return response.data;
  }

  /**
   * Update existing customer
   */
  async updateCustomer(id: string, data: UpdateCustomerDto): Promise<Customer> {
    const response = await apiClient.patch<Customer>(`${this.basePath}/${id}`, data);
    return response.data;
  }

  /**
   * Fetch own customer profile (for customer role users)
   */
  async getOwnProfile(includeVehicles: boolean = true): Promise<Customer> {
    const response = await apiClient.get<any>(`${this.basePath}/me`, {
      params: { include_vehicles: includeVehicles },
    });

    // Transformation handled automatically by apiClient interceptors
    return response.data;
  }

  /**
   * Update own customer profile (for customer role users)
   */
  async updateOwnProfile(data: UpdateCustomerDto): Promise<Customer> {
    const response = await apiClient.patch<Customer>(`${this.basePath}/me`, data);
    return response.data;
  }

  /**
   * Delete customer
   *
   * SCHEMA MIGRATION: Automatically removes customer_id from vehicles
   * Migration Date: 2025-11-03
   * - No force parameter needed
   * - Vehicles are cleaned up automatically (customer_id set to null)
   * - Seamless deletion without confirmation
   *
   * @param id - Customer ID to delete
   */
  async deleteCustomer(id: string): Promise<void> {
    await apiClient.delete(`${this.basePath}/${id}`);
  }

  /**
   * Search customers with debounce-friendly implementation
   */
  async searchCustomers(query: string, limit = 10): Promise<Customer[]> {
    const params: CustomerListParams = {
      search: query,
      limit,
      page: 1,
    };

    const result = await this.getCustomers(params);
    return result.customers;
  }

  /**
   * Delete a truck from a customer's vehicle list
   * SCHEMA MIGRATION: Uses vehicles API endpoint, not customer-based endpoint
   */
  async deleteTruck(_customerId: string, truckId: string): Promise<void> {
    await apiClient.delete(`/api/v1/vehicles/${truckId}`);
  }

  /**
   * Create a new truck/vehicle for a customer
   * FIXED: 2025-10-09 - Now uses apiClient with automatic CSRF injection
   */
  async createTruck(customerId: string): Promise<any> {
    // SCHEMA MIGRATION: Vehicles now use truck_name instead of make/model/year
    const response = await apiClient.post('/api/v1/vehicles', {
      customer_id: customerId,
      truck_name: 'New Vehicle',
    });

    return response.data;
  }

  /**
   * Update a truck/vehicle
   */
  async updateTruck(truckId: string, updates: {
    name?: string;
    vin?: string;
    licensePlate?: string;
    registrationDate?: string;
    biAnnualDate?: string;
  }): Promise<any> {
    // Parse the name into make and model if provided
    const payload: any = {};

    if (updates.name) {
      // SCHEMA MIGRATION: Vehicles now use truck_name instead of make/model
      payload.truck_name = updates.name.trim();
    }

    if (updates.vin !== undefined) {
      payload.vin = updates.vin;
    }

    if (updates.licensePlate !== undefined) {
      payload.license_plate = updates.licensePlate;
    }

    if (updates.registrationDate !== undefined) {
      payload.registration_date = updates.registrationDate;
    }

    if (updates.biAnnualDate !== undefined) {
      payload.bi_annual_date = updates.biAnnualDate;
    }

    const response = await apiClient.put(`/api/v1/vehicles/${truckId}`, payload);
    return response.data;
  }

  /**
   * Get service history for a vehicle
   */
  async getServiceHistory(vehicleId: string): Promise<ServiceHistoryEntry[]> {
    const response = await apiClient.get<ServiceHistoryEntry[]>(
      `/api/v1/vehicles/${vehicleId}/service-history`
    );
    return response.data;
  }

  /**
   * Add a service history entry to a vehicle
   */
  async addServiceHistory(vehicleId: string, data: ServiceHistoryCreateDto): Promise<ServiceHistoryEntry> {
    const response = await apiClient.post<ServiceHistoryEntry>(
      `/api/v1/vehicles/${vehicleId}/service-history`,
      data
    );
    return response.data;
  }

  /**
   * Update a service history entry
   */
  async updateServiceHistory(vehicleId: string, entryId: string, data: ServiceHistoryUpdateDto): Promise<ServiceHistoryEntry> {
    const response = await apiClient.put<ServiceHistoryEntry>(
      `/api/v1/vehicles/${vehicleId}/service-history/${entryId}`,
      data
    );
    return response.data;
  }

  /**
   * Delete a service history entry
   */
  async deleteServiceHistory(vehicleId: string, entryId: string): Promise<void> {
    await apiClient.delete(`/api/v1/vehicles/${vehicleId}/service-history/${entryId}`);
  }
}

export const customerService = new CustomerService();
export default customerService;
